import { useState, useCallback, useMemo } from "react";

export const ChangeType = {
    NONE: "none",
    INSERT: "insert",
    UPDATE: "update",
    DELETE: "delete",
};

export function useStagedChanges() {
    const [inserts, setInserts] = useState([]);
    const [updates, setUpdates] = useState(new Map());
    const [deletes, setDeletes] = useState(new Set());

    const insertIds = useMemo(
        () => new Set(inserts.map((r) => r.id)),
        [inserts]
    );

    const stageInsert = useCallback((record) => {
        setInserts((prev) => [...prev, record]);
    }, []);

    const updateInsert = useCallback((recordId, updates) => {
        setInserts((prev) => prev.map((r) =>
            r.id === recordId ? { ...r, ...updates } : r
        ));
    }, []);

    const stageUpdate = useCallback((recordId, original, modified) => {
        setUpdates((prev) => new Map(prev).set(recordId, { original, modified }));
    }, []);

    const stageDelete = useCallback((recordIds) => {
        setDeletes((prev) => new Set([...prev, ...recordIds]));
    }, []);

    const unstageDelete = useCallback((recordId) => {
        setDeletes((prev) => {
            const next = new Set(prev);
            next.delete(recordId);
            return next;
        });
    }, []);

    const reset = useCallback(() => {
        setInserts([]);
        setUpdates(new Map());
        setDeletes(new Set());
    }, []);

    const getChangeType = useCallback((recordId, isNew) => {
        if (deletes.has(recordId)) return ChangeType.DELETE;
        if (isNew || insertIds.has(recordId)) return ChangeType.INSERT;
        if (updates.has(recordId)) return ChangeType.UPDATE;
        return ChangeType.NONE;
    }, [insertIds, updates, deletes]);

    const hasPendingChanges = useMemo(() => {
        return inserts.length > 0 || updates.size > 0 || deletes.size > 0;
    }, [inserts, updates, deletes]);

    return {
        inserts, updates, deletes,
        stageInsert, updateInsert, stageUpdate, stageDelete, unstageDelete,
        reset, getChangeType, hasPendingChanges,
    };
}
